import { useEffect, useState, useCallback } from "react";
import { useApp } from "../context";
import { Heading, Table } from "../components/Bits";
import { api, apiError, ROLE_LABEL } from "../lib/api";

const CreateUser = ({ onCreated }) => {
  const { notify } = useApp();
  const submit = async e => {
    e.preventDefault();
    const form = e.target;
    const body = Object.fromEntries(new FormData(form));
    try {
      await api.post("/admin/users", body);
      form.reset();
      notify(`Account created for ${body.email}.`);
      onCreated();
    } catch (err) {
      notify(apiError(err));
    }
  };
  return (
    <div className="card">
      <h2>Create account</h2>
      <form className="formgrid" onSubmit={submit} data-testid="create-user-form">
        <label>Full name<input name="name" required maxLength={80} data-testid="create-user-name" /></label>
        <label>Email<input name="email" type="email" required data-testid="create-user-email" /></label>
        <label>Role
          <select name="role" defaultValue="maker" data-testid="create-user-role">
            <option value="maker">Operations maker</option>
            <option value="checker">Finance checker</option>
            <option value="admin">Administrator</option>
          </select>
        </label>
        <label>Temporary password (min 8 characters)
          <input name="password" type="password" minLength={8} required autoComplete="new-password" data-testid="create-user-password" />
        </label>
        <div className="full footer" style={{ marginTop: 0 }}>
          <button type="submit" className="primary" data-testid="create-user-submit-btn">Create account</button>
        </div>
      </form>
    </div>
  );
};

const UserRow = ({ u, self, reload }) => {
  const { notify, openModal } = useApp();
  const patch = async body => {
    try {
      await api.patch(`/admin/users/${u.id}`, body);
      notify("Account updated.");
      reload();
    } catch (err) {
      notify(apiError(err));
    }
  };
  return (
    <tr data-testid={`user-row-${u.email}`}>
      <td><strong>{u.name}</strong><small>{u.email}{self ? " · you" : ""}</small></td>
      <td>
        <select value={u.role} disabled={self} onChange={e => patch({ role: e.target.value })}
          data-testid={`user-role-select-${u.email}`} style={{ maxWidth: 190 }}>
          {Object.entries(ROLE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </td>
      <td>
        <span className={`pill ${u.active ? "" : "neutral"}`} data-testid={`user-status-${u.email}`}>{u.active ? "Active" : "Disabled"}</span>
        {u.must_change_password && <small className="muted" data-testid={`user-temp-password-${u.email}`}>Temporary password, not yet changed</small>}
      </td>
      <td><div className="actions">
        <button disabled={self} onClick={() => patch({ active: !u.active })} data-testid={`user-toggle-btn-${u.email}`}>
          {u.active ? "Disable" : "Enable"}
        </button>
        <button onClick={() => openModal({ type: "password", user: u, reload })} data-testid={`user-password-btn-${u.email}`}>Reset password</button>
        <button className="danger" disabled={self} onClick={() => openModal({ type: "deleteUser", user: u, reload })}
          data-testid={`user-delete-btn-${u.email}`}>Delete</button>
      </div></td>
    </tr>
  );
};

export default function Users() {
  const { user, notify } = useApp();
  const [users, setUsers] = useState([]);
  const reload = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (err) {
      notify(apiError(err));
    }
  }, [notify]);

  useEffect(() => {
    reload();
  }, [reload]);

  return (
    <>
      <Heading title="Users" description="Create maker and checker accounts. Only administrators can see this screen." />
      <div className="notice warning">
        Each account's role decides what it can do: makers create cases and request advances, checkers approve
        and import receipts, administrators manage accounts only. Loan data still lives in each browser.
      </div>
      <CreateUser onCreated={reload} />
      <div className="card space">
        <h2>Accounts</h2>
        <Table testId="users-table" headers={["User", "Role", "Status", "Actions"]}>
          {users.map(u => <UserRow key={u.id} u={u} self={u.id === user.id} reload={reload} />)}
        </Table>
      </div>
    </>
  );
}
